import { useState, useMemo, useCallback } from "react";

type UseVirtualizedListProps<T> = {
  items: T[];
  itemHeight: number;
  viewportHeight: number;
  buffer?: number;
  searchValue?: string;
  filterFn?: (item: T, searchValue: string) => boolean;
};

export const useVirtualizedList = <T>({
  items,
  itemHeight,
  viewportHeight,
  buffer = 20,
  searchValue = "",
  filterFn,
}: UseVirtualizedListProps<T>) => {
  const [scrollTop, setScrollTop] = useState(0);

  const filteredItems = useMemo(() => {
    if (!searchValue || !filterFn) return items;
    return items.filter((item) => filterFn(item, searchValue));
  }, [items, searchValue, filterFn]);

  const totalItems = filteredItems.length;
  const visibleCount = Math.ceil(viewportHeight / itemHeight);
  const rawStart = Math.floor(scrollTop / itemHeight);
  const startIndex = Math.max(0, rawStart - buffer);
  const endIndex = Math.min(totalItems, rawStart + visibleCount + buffer);

  const visibleItems = filteredItems.slice(startIndex, endIndex);

  const handleScroll = useCallback((scrollTopValue: number) => {
    setScrollTop(scrollTopValue);
  }, []);

  const containerHeight = totalItems * itemHeight;

  return {
    visibleItems,
    startIndex,
    containerHeight,
    handleScroll,
    filteredItems,
  };
};
